/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2022, Zumper
 * @description  RelatedPropertiesLazyLoadableSection.jsx
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { BREAKPOINT_480 } from '@zcode-ui/themes'
import { createLazyLoadableSection } from '@zcode/lazy-loadable-section'
import { ErrorFallback, LoadingFallback } from '@zcode/nr/es/shared/components/Fallback'
import type { ErrorBoundaryProps } from 'react-error-boundary'

import { logger } from '../../logger'

const fallbackStyle = css`
  height: 745px;
  @media (min-width: ${BREAKPOINT_480}) {
    height: 777px;
  }
`

const onRenderError: ErrorBoundaryProps['onError'] = (error, info) => {
  logger.error('render error for RelatedPropertiesLazyLoadableSection', info, error)
}

const onLoadingError = (error: unknown) => {
  logger.error('loading error for RelatedPropertiesLazyLoadableSection', error)
}

const loadFn = /* #__LOADABLE__ */ async () =>
  await import(/* webpackChunkName: "RelatedProperties" */ './RelatedProperties')

const message = `Sorry, we couldn't load similar apartments...`

const handleReload = async () => await RelatedPropertiesLazyLoadableSection.reload()

const RelatedPropertiesErrorFallback = () => (
  <ErrorFallback css={fallbackStyle} message={message} onClickReload={handleReload} />
)
export const RelatedPropertiesLoadingFallback = () => (
  <LoadingFallback css={fallbackStyle} name="similar section" />
)

const options = {
  errorFallback: RelatedPropertiesErrorFallback,
  loadingFallback: RelatedPropertiesLoadingFallback,
  onRenderError,
  onLoadingError,
}

export const RelatedPropertiesLazyLoadableSection = createLazyLoadableSection(loadFn, options)
